import { useEffect, useMemo, useState } from "react";
import type {
  HandDetector,
  MediaPipeHandsMediaPipeModelConfig,
} from "@tensorflow-models/hand-pose-detection";
import type { ObjectDetection } from "@tensorflow-models/coco-ssd";

const MEDIAPIPE_ASSET_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/hands";
const envSolutionPath = (import.meta.env?.VITE_MEDIAPIPE_HANDS_PATH ?? "").trim();
const MEDIA_PIPE_SOLUTION_PATH = (envSolutionPath || MEDIAPIPE_ASSET_URL).replace(/\/+$/, "");

const MAX_HANDS = 2;

type HandDetectorRuntimeConfig = MediaPipeHandsMediaPipeModelConfig;
type HandDetectorRuntime = HandDetectorRuntimeConfig["runtime"];

const HAND_DETECTOR_CONFIG: HandDetectorRuntimeConfig = {
  runtime: "mediapipe",
  solutionPath: MEDIA_PIPE_SOLUTION_PATH,
  modelType: "full",
  maxHands: MAX_HANDS,
};

type MemoryInfo = {
  usedJSHeapSize: string;
  totalJSHeapSize: string;
  jsHeapSizeLimit: string;
};

type DetectionModels = {
  handDetector: HandDetector;
  objectDetector: ObjectDetection;
};

type DetectionModelsState =
  | {
      status: "idle";
      message: string;
      error: null;
      models: null;
    }
  | {
      status: "loading";
      message: string;
      error: null;
      models: null;
    }
  | {
      status: "ready";
      message: string;
      error: null;
      models: DetectionModels;
    }
  | {
      status: "error";
      message: string;
      error: Error;
      models: null;
    };

const now = (): number => {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
};

const getMemoryInfo = (): MemoryInfo | null => {
  if (typeof performance === "undefined") {
    return null;
  }

  const performanceWithMemory = performance as Performance & {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  };

  if (!performanceWithMemory.memory) {
    return null;
  }

  const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = performanceWithMemory.memory;
  const toMB = (value: number) => (value / 1024 / 1024).toFixed(2);

  return {
    usedJSHeapSize: toMB(usedJSHeapSize),
    totalJSHeapSize: toMB(totalJSHeapSize),
    jsHeapSizeLimit: toMB(jsHeapSizeLimit),
  };
};

const describeRuntime = (runtime: HandDetectorRuntimeConfig) => {
  return `${runtime.runtime} (${runtime.solutionPath})`;
};

const waitForMediaPipeHands = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && (window as any).Hands) {
      resolve();
      return;
    }

    const maxWait = 5_000;
    const startTime = Date.now();
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;
      if (typeof window !== "undefined" && (window as any).Hands) {
        clearInterval(checkInterval);
        resolve();
      } else if (Date.now() - startTime > maxWait) {
        clearInterval(checkInterval);
        reject(new Error("MediaPipe Hands failed to load from script tag"));
      }
    }, 100);
  });
};

const createHandDetector = async (): Promise<{
  handDetector: HandDetector;
  runtime: HandDetectorRuntime;
}> => {
  const tf = await import("@tensorflow/tfjs-core");
  await waitForMediaPipeHands();
  await Promise.all([
    import("@tensorflow/tfjs-backend-webgl"),
    import("@tensorflow/tfjs-backend-cpu"),
  ]);
  const handPoseDetection = await import("@tensorflow-models/hand-pose-detection");
  const handModel = handPoseDetection.SupportedModels.MediaPipeHands;
  const handDetector = await handPoseDetection.createDetector(handModel, HAND_DETECTOR_CONFIG);

  if (typeof tf.setBackend === "function") {
    const currentBackend = tf.getBackend?.();
    if (currentBackend !== "webgl") {
      await tf.setBackend("webgl");
    }
  }

  return { handDetector, runtime: HAND_DETECTOR_CONFIG.runtime };
};

let cachedModels: DetectionModels | null = null;
let modelsPromise: Promise<DetectionModels> | null = null;

async function loadDetectionModels(): Promise<DetectionModels> {
  if (cachedModels) {
    return cachedModels;
  }

  if (!modelsPromise) {
    modelsPromise = (async () => {
      try {
        const memoryBefore = getMemoryInfo();

        const { handDetector, runtime: handRuntime } = await createHandDetector();
        const memoryAfterHand = getMemoryInfo();

        await new Promise((resolve) => setTimeout(resolve, 200));

        const cocoSsd = await import("@tensorflow-models/coco-ssd");
        const objectDetector = await cocoSsd.load({ base: "mobilenet_v1" });

        const memoryAfterObject = getMemoryInfo();

        if (memoryBefore && memoryAfterObject) {
          const totalMemory = (
            parseFloat(memoryAfterObject.usedJSHeapSize) - parseFloat(memoryBefore.usedJSHeapSize)
          ).toFixed(2);
          console.log(
            `Detection models loaded (${describeRuntime(HAND_DETECTOR_CONFIG)}) using ~${totalMemory} MB`,
          );
        } else {
          console.log(
            `Detection models loaded (${describeRuntime(HAND_DETECTOR_CONFIG)})`,
            { runtime: handRuntime },
          );
        }

        cachedModels = { handDetector, objectDetector };
        return cachedModels;
      } catch (err) {
        cachedModels = null;
        modelsPromise = null;
        const error = err instanceof Error ? err : new Error(String(err));
        throw error;
      }
    })();
  }

  return modelsPromise;
}

export type ModelsForHandsDetectionsHook = {
  isPreparing: boolean;
  hasError: boolean;
  error: Error | null;
  statusMessage: string;
  handDetector: HandDetector | null;
  objectDetector: ObjectDetection | null;
};

const useModelsforHandsDetections = (): ModelsForHandsDetectionsHook => {
  const [state, setState] = useState<DetectionModelsState>({
    status: "idle",
    message: "Preparing interview…",
    error: null,
    models: null,
  });

  useEffect(() => {
    let isMounted = true;

    const initialise = async () => {
      if (cachedModels) {
        if (isMounted) {
          setState({
            status: "ready",
            message: "Detections ready.",
            error: null,
            models: cachedModels,
          });
        }
        return;
      }

      if (isMounted) {
        setState({
          status: "loading",
          message: "Preparing interview…",
          error: null,
          models: null,
        });
      }

      try {
        const models = await loadDetectionModels();

        if (isMounted) {
          setState({
            status: "ready",
            message: "Detections ready.",
            error: null,
            models,
          });
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        if (isMounted) {
          setState({
            status: "error",
            message: "Failed to prepare interview. Please refresh.",
            error,
            models: null,
          });
        }
      }
    };

    void initialise();

    return () => {
      isMounted = false;
    };
  }, []);

  return useMemo<ModelsForHandsDetectionsHook>(() => {
    if (state.status === "ready" && state.models) {
      return {
        isPreparing: false,
        hasError: false,
        error: null,
        statusMessage: state.message,
        handDetector: state.models.handDetector,
        objectDetector: state.models.objectDetector,
      };
    }

    if (state.status === "error") {
      return {
        isPreparing: false,
        hasError: true,
        error: state.error,
        statusMessage: state.message,
        handDetector: null,
        objectDetector: null,
      };
    }

    return {
      isPreparing: true,
      hasError: false,
      error: null,
      statusMessage: state.message,
      handDetector: null,
      objectDetector: null,
    };
  }, [state]);
};

export default useModelsforHandsDetections;
